import {
  getFunctions,
  getSignalMetadata,
  normalizeMode,
  parseMetaJson,
} from "@/features/devices/adapters";
// `SignalType` e o catálogo entram na derivação da versão de estrutura; ver
// `declaredSignalTagsForDashboard`.
import type {
  DeviceDashboardResponse,
  MetaFunctionEntry,
} from "@/features/devices/types";
import type { SignalType } from "@/features/machines/types";
import type { StarterSignalDefinition } from "@/features/widgets/components/WidgetPanel";
import { resolveSignalKind } from "@ghm/mqtt-contract";

import { catalogEntriesForMode, widgetTypeForKind } from "./widgetCatalog";

/**
 * Composição dirigida por meta (P1, base da auto-renderização). O hardware
 * declara suas funções no `/meta` retido; estas funções puras traduzem isso
 * nos "starter signals" que viram widgets automáticos — sem este módulo a
 * lógica vivia inline na página e não era testável. Golden-testado em
 * `composition.test.ts`.
 */

/** Ordena funções por porta (e modo como desempate) para layout estável. */
export function sortMetaFunctionsByPort(
  functions: MetaFunctionEntry[],
): MetaFunctionEntry[] {
  return [...functions].sort((left, right) => {
    const leftPort = typeof left.p === "number" ? left.p : Number.POSITIVE_INFINITY;
    const rightPort = typeof right.p === "number" ? right.p : Number.POSITIVE_INFINITY;
    if (leftPort !== rightPort) return leftPort - rightPort;
    return normalizeMode(left.m).localeCompare(normalizeMode(right.m));
  });
}

type SignalSnapshot = { s?: unknown; acc?: unknown; v?: unknown } | undefined;

/** "Função · Sinal", com fallback gracioso quando um dos dois falta. */
function composeLabel(
  functionLabel: string,
  signalLabel: string | undefined,
): string | null {
  if (functionLabel && signalLabel) return `${functionLabel} · ${signalLabel}`;
  return signalLabel || functionLabel || null;
}

/**
 * Uma função "tem" um sinal quando o meta o DECLARA (`fn.values[signal]`,
 * fonte da verdade do firmware) ou, na ausência de declaração, quando o
 * snapshot mais recente traz valor para ele. Sem snapshot e sem declaração,
 * assume otimista (boot) — o widget aparece em "aguardando".
 */
function functionHasSignal(
  fn: MetaFunctionEntry,
  signal: SignalType,
  snapshot: SignalSnapshot,
): boolean {
  const values = fn.values;
  if (values && !Array.isArray(values)) {
    return (values as Record<string, unknown>)[signal] !== undefined;
  }
  if (!snapshot) return true;
  const raw = signal === "s" ? snapshot.s : signal === "v" ? snapshot.v : snapshot.acc;
  return raw !== null && raw !== undefined;
}

/**
 * A partir do `/meta` de UM dispositivo, deriva os starter signals — agora o
 * CONJUNTO CURADO do catálogo por função (P1), não só o sinal primário. Um
 * encoder, por exemplo, vira velocidade + distância + sentido. Cada sinal
 * carrega o tipo de widget coerente (do catálogo) e label/unidade do meta.
 * Determinístico: mesma entrada, mesmo conjunto de widgets.
 */
const SIGNAL_TYPES = ["s", "acc", "v"] as const;

/**
 * Modos cujo significado é DADO DECLARADO, não estrutura física.
 *
 * Numa DI, num contador ou num encoder, a porta É a identidade: `di1` é o fio
 * que está no `di1`, e a grandeza não muda de natureza (um medidor de pulso
 * continua medindo vazão). Já um ponto `MB` é um registrador escolhido num
 * mapa — a mesma porta 6 pode ler tensão hoje e temperatura amanhã, sem que a
 * ESTRUTURA de sinais (`v` + `s`) mude em nada. `AIN` está no mesmo grupo: a
 * troca do sensor em `ai1` muda a grandeza sem mexer nas tags.
 *
 * Para esses dois, portanto, o conjunto de tags não basta para versionar o
 * layout: é preciso levar em conta o DESCRITOR declarado (rótulo + unidade).
 * Trade-off assumido: renomear um ponto Modbus no configurador, mesmo sem
 * remapear, conta como mudança de estrutura e recompõe o painel. O dashboard
 * não tem como distinguir "renomeou" de "remapeou", e errar para o lado de
 * recompor é preferível a manter um card rotulado "Tensão L1 (V)" exibindo a
 * temperatura do óleo.
 */
const DECLARATION_DEFINED_MODES = new Set(["AIN", "MB"]);

/**
 * FNV-1a de 32 bits em hex — determinístico, curto e sem dependência. Só
 * precisa ser estável e distinguir descritores diferentes; não é segurança.
 * Mantém a versão limitada em tamanho mesmo com 16 pontos Modbus de rótulo
 * livre, já que ela viaja como query param e como sufixo de chave no
 * `localStorage`.
 */
function fingerprint(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Tags `G{slot}_P{port}_{sinal}` de TODOS os sinais que o `/meta` DECLARA,
 * independente do catálogo de widgets automáticos.
 *
 * A distinção importa. A versão de estrutura protege os widgets SALVOS, e um
 * widget salvo pode referenciar qualquer sinal que o operador conseguiu
 * escolher no editor — que lê `functions[].values` do meta, não o catálogo.
 * Derivar a versão dos STARTERS deixava de fora todo sinal escolhível que não
 * auto-materializa:
 *
 *  - `MB` não gera starter nenhum (é protocolo, não grandeza), então trocar o
 *    mapa Modbus inteiro — porta 6 deixa de ser "Tensão L1" e vira
 *    "Temperatura do óleo" — não mudava a versão. O card salvo do operador
 *    continuava com o rótulo e a unidade antigos apontando para outro
 *    registrador, em silêncio. É a mesma reatribuição silenciosa que motivou
 *    DECLARAR a porta Modbus em vez de derivá-la da posição na lista
 *    (ver `configurador/docs/mqtt-contract.md`, seção `MB`).
 *  - `RST` e `PM` declaram `s` no meta mas o catálogo não o materializa;
 *    um widget de estado salvo neles tinha a mesma exposição, em escala menor.
 *
 * Efeito colateral assumido: para configurações com `MB`, `RST` ou `PM` a
 * versão muda uma vez, e o layout salvo sob a chave antiga é recomposto a
 * partir do meta atual. Configurações só de `HSC`/`DI`/`CT` — o caso do piloto
 * em campo — produzem exatamente a mesma string de antes e não perdem nada.
 *
 * Ganho secundário: a versão deixa de depender do catálogo de widgets, então
 * mexer no catálogo no futuro não invalida mais o layout de ninguém.
 */
export function declaredSignalTagsForDashboard(
  dashboard: DeviceDashboardResponse,
  slotIndex: number,
): string[] {
  const parsed = parseMetaJson(dashboard.currentMeta);
  const functions = sortMetaFunctionsByPort(getFunctions(parsed.data));

  return functions.flatMap((fn): string[] => {
    if (typeof fn.p !== "number") return [];
    const values = fn.values && !Array.isArray(fn.values) ? fn.values : null;
    // Sem `values` declarado (firmware antigo ou meta parcial) não há o que
    // enumerar: cai no conjunto do catálogo, que era o comportamento anterior.
    const signals: readonly SignalType[] = values
      ? SIGNAL_TYPES.filter(
          (signal) => (values as Record<string, unknown>)[signal] !== undefined,
        )
      : catalogEntriesForMode(fn.m ?? undefined).map((entry) => entry.signal);

    return signals.map((signal) => `G${slotIndex}_P${fn.p}_${signal}`);
  });
}

/**
 * Assinatura dos descritores declarados pelos modos definidos por dado
 * (`AIN`/`MB`) — ver {@link DECLARATION_DEFINED_MODES}. `null` quando a
 * configuração não tem nenhum deles, e nesse caso a versão de estrutura fica
 * EXATAMENTE como era antes desta mudança: um painel só de HSC/DI/CT (o caso
 * do piloto em campo) não perde layout salvo.
 */
export function declarationFingerprintForDashboards(
  dashboards: DeviceDashboardResponse[],
): string | null {
  const parts = dashboards.flatMap((dashboard, slotIndex) => {
    const parsed = parseMetaJson(dashboard.currentMeta);
    return sortMetaFunctionsByPort(getFunctions(parsed.data)).flatMap((fn): string[] => {
      if (typeof fn.p !== "number") return [];
      if (!DECLARATION_DEFINED_MODES.has(normalizeMode(fn.m))) return [];
      const values = fn.values && !Array.isArray(fn.values) ? fn.values : null;
      if (!values) return [];

      return SIGNAL_TYPES.filter(
        (signal) => (values as Record<string, unknown>)[signal] !== undefined,
      ).map((signal) => {
        const metadata = getSignalMetadata(fn, signal);
        return `G${slotIndex}_P${fn.p}_${signal}=${metadata.label ?? ""}/${metadata.unit ?? ""}`;
      });
    });
  });

  if (parts.length === 0) return null;
  return fingerprint(parts.sort().join("|"));
}

/**
 * Versão da ESTRUTURA DE SINAIS de um workflow: o conjunto ordenado e único de
 * tags que o hardware DECLARA. Usada para versionar o layout/config do operador
 * — em vez do `configHashHex` inteiro. Motivo: o hash muda em qualquer mudança
 * de config (bump de firmware, cadência, etc.), e versionar por ele descartava
 * os widgets do operador (tamanhos, tipos) mesmo quando os SINAIS eram
 * idênticos. Chavear pela estrutura de sinais preserva o layout em mudanças
 * cosméticas e só recompõe o painel quando os sinais de fato mudam — mantendo
 * o isolamento contra widgets fantasma. `null` sem sinais.
 */
export function deriveSignalStructureVersion(
  dashboards: DeviceDashboardResponse[],
): string | null {
  const tags = dashboards.flatMap((dashboard, slotIndex) =>
    declaredSignalTagsForDashboard(dashboard, slotIndex),
  );
  if (tags.length === 0) return null;

  const structure = [...new Set(tags)].sort().join("|");
  // O sufixo só existe quando há AIN/MB. Sem eles a string é byte a byte a
  // mesma de antes — nenhum painel em campo troca de chave por esta mudança.
  const declaration = declarationFingerprintForDashboards(dashboards);
  return declaration ? `${structure}#${declaration}` : structure;
}

export function buildStarterSignalsForDashboard(
  dashboard: DeviceDashboardResponse,
  slotIndex: number,
): StarterSignalDefinition[] {
  const parsed = parseMetaJson(dashboard.currentMeta);
  const functions = sortMetaFunctionsByPort(getFunctions(parsed.data));

  return functions.flatMap((fn): StarterSignalDefinition[] => {
    if (typeof fn.p !== "number") return [];
    const portIndex = fn.p;

    const snapshot = dashboard.latestPorts.find((port) => port.portIndex === portIndex);
    const mode = fn.m ?? snapshot?.mode;
    const functionLabel = typeof fn.label === "string" ? fn.label.trim() : "";

    return catalogEntriesForMode(mode)
      .filter((entry) => functionHasSignal(fn, entry.signal, snapshot))
      .map((entry): StarterSignalDefinition => {
        const metadata = getSignalMetadata(fn, entry.signal);
        const signalLabel = metadata.label?.trim();
        // A SEMÂNTICA declarada vence o palpite do catálogo. Sem isto, um
        // `kWh total` lido do medidor — que chega como `v` porque é uma
        // leitura, não um totalizador do ESP — nasceria como velocímetro.
        const kind = resolveSignalKind(
          typeof mode === "string" ? mode : undefined,
          entry.signal,
          metadata,
        );
        return {
          deviceId: dashboard.device.id,
          slotIndex,
          portIndex,
          mode,
          signal: entry.signal,
          widgetType:
            entry.signal === "v" ? widgetTypeForKind(kind) : entry.widgetType,
          kind,
          // Faixa e casas vem do configurador pelo `/meta`. Sem elas o
          // dashboard inventava a escala a partir da primeira leitura.
          min: metadata.min ?? null,
          max: metadata.max ?? null,
          decimals: metadata.decimals ?? null,
          // Com vários widgets por função, o card precisa dizer a função E o
          // sinal: "Encoder esteira · Velocidade". Cai para só um dos dois
          // quando o outro falta, e para null (default) quando ambos faltam.
          label: composeLabel(functionLabel, signalLabel),
          unit: metadata.unit ?? null,
        };
      });
  });
}
